import React, { useMemo } from 'react'
import { Button, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap'
import { Globe, MessageSquare, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useRenewalsList } from '@data/renewals/renewals'
import { ICustomerRenewalRow } from '@data/renewals/model/renewals.model'
import './Renewals.scss'

export type DueWindow = 7 | 30 | 60 | 'overdue'
export type SortKey = 'soonest' | 'name' | 'devices' | 'overdue'
export type HealthBucket = 'all' | 'healthy' | 'upcoming' | 'overdue' | 'restricted' | 'no_subscription'

export interface RenewalsListFilters {
  region: string
  health: HealthBucket
  sort: SortKey
  search: string
  dueWindow: DueWindow
}

export const DEFAULT_LIST_FILTERS: RenewalsListFilters = {
  region: 'all',
  health: 'all',
  sort: 'soonest',
  search: '',
  dueWindow: 30,
}

const DUE_WINDOWS: { key: DueWindow; label: string }[] = [
  { key: 7, label: '7d' },
  { key: 30, label: '30d' },
  { key: 60, label: '60d' },
  { key: 'overdue', label: 'Overdue' },
]

const REGION_OPTIONS = [
  { value: 'all', label: 'All Regions' },
  { value: 'EUR', label: 'SMART FLOW (EU)' },
  { value: 'GBP', label: 'SMART FLOW (UK)' },
  { value: 'USD', label: 'SMART FLOW (USA)' },
]

const HEALTH_OPTIONS: { value: HealthBucket; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'no_subscription', label: 'No Subscription' },
  { value: 'restricted', label: 'Restricted' },
]

function deriveHealth(r: ICustomerRenewalRow): Exclude<HealthBucket, 'all'> {
  if (r.overdue_count > 0) return 'overdue'
  if (r.no_subscription > 0) return 'no_subscription'
  if (r.upcoming_count > 0) return 'upcoming'
  return 'healthy'
}

interface CountRow {
  upcoming_count: number
  overdue_count: number
  no_subscription: number
}

// The Upcoming / Overdue / No-Subscription cells, shared by the billing-entity
// and location rows (the account header renders its own bolded variant).
function renderCountCells(row: CountRow, dueWindow: DueWindow) {
  return (
    <>
      <td>
        <div>{row.upcoming_count}</div>
        {row.upcoming_count > 0 && (
          <span className="sf-row-annotation sf-row-annotation--upcoming">
            {dueWindow === 'overdue' ? '' : `${dueWindow}d`}
          </span>
        )}
      </td>
      <td>
        <div className={row.overdue_count > 0 ? 'sf-past-due fw-semibold' : 'text-muted'}>
          {row.overdue_count}
        </div>
        {row.overdue_count > 0 && (
          <span className="sf-row-annotation sf-row-annotation--overdue">Overdue</span>
        )}
      </td>
      <td className={row.no_subscription > 0 ? 'text-primary fw-semibold' : 'text-muted'}>
        {row.no_subscription}
      </td>
    </>
  )
}

interface Props {
  filters: RenewalsListFilters
  onFiltersChange: (next: RenewalsListFilters) => void
}

const RenewalsList: React.FC<Props> = ({ filters, onFiltersChange }) => {
  const { region, health, sort, search, dueWindow } = filters
  const setField = <K extends keyof RenewalsListFilters>(key: K, value: RenewalsListFilters[K]) =>
    onFiltersChange({ ...filters, [key]: value })

  const apiWindow = dueWindow === 'overdue' ? 365 : dueWindow
  const { data: renewals, isLoading } = useRenewalsList(apiWindow)
  const navigate = useNavigate()

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = (renewals ?? []).filter((r) => {
      // Region now lives on the billing entities; an account matches if any of
      // its entities is in the selected region.
      if (region !== 'all' && !r.billing_entities.some((be) => be.region === region)) {
        return false
      }
      if (health !== 'all') {
        // The "restricted" bucket should also surface true restricted (overdue devices).
        if (health === 'restricted' && r.overdue_count === 0) return false
        if (health !== 'restricted' && deriveHealth(r) !== health) return false
      }
      if (dueWindow === 'overdue' && r.overdue_count === 0) return false
      if (q) {
        return (
          r.account_name.toLowerCase().includes(q) ||
          r.billing_entities.some((be) => be.name.toLowerCase().includes(q))
        )
      }
      return true
    })

    rows = [...rows].sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.account_name.localeCompare(b.account_name)
        case 'devices':
          return b.total_devices - a.total_devices
        case 'overdue':
          return b.overdue_count - a.overdue_count
        case 'soonest':
        default:
          return b.upcoming_count - a.upcoming_count
      }
    })

    return rows
  }, [renewals, region, health, sort, search, dueWindow])

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Renewals List</h4>
        <span className="text-muted small d-inline-flex align-items-center gap-2">
          <Globe size={14} />
          ALL ENTITIES
        </span>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <Form.Label className="text-muted small fw-semibold">REGION</Form.Label>
              <Form.Select value={region} onChange={(e) => setField('region', e.target.value)}>
                {REGION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Label className="text-muted small fw-semibold">GLOBAL HEALTH</Form.Label>
              <Form.Select
                value={health}
                onChange={(e) => setField('health', e.target.value as HealthBucket)}
              >
                {HEALTH_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Label className="text-muted small fw-semibold">SORT BY</Form.Label>
              <Form.Select value={sort} onChange={(e) => setField('sort', e.target.value as SortKey)}>
                <option value="soonest">Soonest Renewal</option>
                <option value="name">Customer Name</option>
                <option value="devices">Total Devices</option>
                <option value="overdue">Most Overdue</option>
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label className="text-muted small fw-semibold">SEARCH</Form.Label>
              <InputGroup>
                <InputGroup.Text><Search size={14} /></InputGroup.Text>
                <Form.Control
                  placeholder="Customer name..."
                  value={search}
                  onChange={(e) => setField('search', e.target.value)}
                />
              </InputGroup>
            </div>
            <div className="col-md-3">
              <Form.Label className="text-muted small fw-semibold">DUE WINDOW</Form.Label>
              <div>
                {DUE_WINDOWS.map((w) => (
                  <span
                    key={w.key}
                    className={`sf-window-pill ${dueWindow === w.key ? 'active' : ''}`}
                    onClick={() => setField('dueWindow', w.key)}
                  >
                    {w.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        {isLoading ? (
          <div className="p-3">
            <Spinner animation="border" />
          </div>
        ) : (
          <Table responsive hover className="mb-0 align-middle">
            <thead>
              <tr className="text-muted small">
                <th>CUSTOMER / BILLING ENTITY / LOCATION</th>
                <th>DEVICES (TOTAL)</th>
                <th>UPCOMING (DEVICES)</th>
                <th>OVERDUE (DEVICES)</th>
                <th>NO SUBSCRIPTION (DEVICES)</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    No accounts found
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <React.Fragment key={r.account_id}>
                    {/* Account header — rolled-up totals across all billing entities. */}
                    <tr className="sf-acct-row">
                      <td>
                        <div className="fw-semibold">{r.account_name}</div>
                      </td>
                      <td className="fw-semibold">{r.total_devices}</td>
                      <td className="fw-semibold">{r.upcoming_count}</td>
                      <td className={r.overdue_count > 0 ? 'sf-past-due fw-semibold' : 'fw-semibold text-muted'}>
                        {r.overdue_count}
                      </td>
                      <td className={r.no_subscription > 0 ? 'text-primary fw-semibold' : 'fw-semibold text-muted'}>
                        {r.no_subscription}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-muted small d-inline-flex align-items-center gap-1">
                            <MessageSquare size={14} />
                            {r.comment_count}
                          </span>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => navigate(`/renewals/${r.account_id}`)}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>

                    {/* Billing entity sub-groups + their locations. */}
                    {r.billing_entities.map((be) => (
                      <React.Fragment key={`${r.account_id}-${be.billing_entity_id ?? 'unassigned'}`}>
                        <tr className="sf-entity-row">
                          <td>
                            <div className="sf-indent-1 d-inline-flex align-items-center gap-2">
                              <span className="fw-semibold">{be.name}</span>
                              {be.region && (
                                <span className="sf-region-badge">{be.region}</span>
                              )}
                            </div>
                          </td>
                          <td className="fw-semibold">{be.total_devices}</td>
                          {renderCountCells(be, dueWindow)}
                          <td />
                        </tr>
                        {be.locations.map((loc) => (
                          <tr key={loc.location_id} className="sf-loc-row">
                            <td>
                              <span className="sf-indent-2 text-muted">{loc.location_name}</span>
                            </td>
                            <td>{loc.total_devices}</td>
                            {renderCountCells(loc, dueWindow)}
                            <td />
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  )
}

export default RenewalsList
